import { useEffect, useMemo, useRef, useState } from "react";
import { BotOff, Check, Loader2, Plus, RotateCcw, Save, Trash2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  DEFAULT_THREAD_GROUPS,
  GROUP_COLOR_PALETTE,
  THREAD_GROUP_FLAG_IGNORE,
  getGroupColor,
  getThreadGroupFlags,
  groupHasFlag,
  nextUnusedColorId,
  slugifyGroupId,
} from "@/lib/thread-groups";
import { putMessagesSettings } from "@/lib/api/settings";
import {
  getThreadGroupsConfig,
  hydrateThreadGroupsFromServer,
  subscribeThreadGroups,
} from "@/lib/stores/thread-groups-store";

function uniqueGroupId(baseId, existing) {
  const taken = new Set(existing.map((g) => g.id));
  if (!taken.has(baseId)) return baseId;
  for (let i = 2; i < 1000; i += 1) {
    const candidate = `${baseId}-${i}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${baseId}-${Date.now()}`;
}

/**
 * Editor for the tenant-wide shared thread groups. Edits accumulate in a local draft and are
 * persisted with an explicit Save (one PUT, not one per keystroke); the saved catalog applies
 * to every staff user. Read-only (canEdit=false) for user types outside the eligible list —
 * the server enforces the same gate on PUT.
 */
export function ThreadGroupsEditor({ canEdit = true }) {
  const [draft, setDraft] = useState(() => getThreadGroupsConfig());
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;

  // Follow store updates (server hydration from the overview/settings fetch) only while the
  // user has no unsaved edits, so a background refresh never clobbers an in-progress draft.
  useEffect(
    () =>
      subscribeThreadGroups((groups) => {
        if (!dirtyRef.current) setDraft(groups);
      }),
    [],
  );

  const editDraft = (next) => {
    setDraft(next);
    setDirty(true);
  };

  const handleNameChange = (id, name) => {
    editDraft(draft.map((g) => (g.id === id ? { ...g, name } : g)));
  };

  const handleColorChange = (id, colorId) => {
    editDraft(draft.map((g) => (g.id === id ? { ...g, colorId } : g)));
  };

  const handleToggleFlag = (id, flagId) => {
    editDraft(
      draft.map((g) => {
        if (g.id !== id) return g;
        const flags = getThreadGroupFlags(g);
        return {
          ...g,
          flags: flags.includes(flagId) ? flags.filter((f) => f !== flagId) : [...flags, flagId],
        };
      }),
    );
  };

  const handleDelete = (id) => {
    editDraft(draft.filter((g) => g.id !== id));
  };

  const handleAdd = () => {
    const baseName = "New group";
    const id = uniqueGroupId(slugifyGroupId(baseName), draft);
    const colorId = nextUnusedColorId(draft);
    editDraft([...draft, { id, name: baseName, colorId, flags: [] }]);
  };

  const handleResetToDefaults = () => {
    editDraft(DEFAULT_THREAD_GROUPS.map((g) => ({ ...g })));
  };

  const handleDiscard = () => {
    setDraft(getThreadGroupsConfig());
    setDirty(false);
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await putMessagesSettings({ threadGroups: draft });
      hydrateThreadGroupsFromServer(res.threadGroups);
      setDraft(res.threadGroups);
      setDirty(false);
      toast.success("Thread groups saved for everyone.");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Could not save thread groups";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Define the groups available in the threads list. Shared with your whole team.
      </p>
      {!canEdit ? (
        <p className="text-xs text-muted-foreground">
          Only eligible staff can edit shared thread groups.
        </p>
      ) : null}

      <ul className="space-y-2">
        {draft.map((group) => (
          <GroupRow
            key={group.id}
            group={group}
            allGroups={draft}
            disabled={!canEdit}
            onNameChange={(name) => handleNameChange(group.id, name)}
            onColorChange={(colorId) => handleColorChange(group.id, colorId)}
            onToggleIgnore={() => handleToggleFlag(group.id, THREAD_GROUP_FLAG_IGNORE)}
            onDelete={() => handleDelete(group.id)}
          />
        ))}
        {draft.length === 0 ? (
          <li className="rounded-md border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">
            No groups defined. Add one to start tagging threads.
          </li>
        ) : null}
      </ul>

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Button type="button" variant="outline" size="sm" onClick={handleAdd} disabled={!canEdit}>
          <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          Add group
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleResetToDefaults}
          disabled={!canEdit}
        >
          <RotateCcw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          Reset to defaults
        </Button>
        <div className="ml-auto flex items-center gap-2">
          {dirty ? (
            <Button type="button" variant="ghost" size="sm" onClick={handleDiscard} disabled={saving}>
              <Undo2 className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              Discard
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={!canEdit || !dirty || saving}
          >
            {saving ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <Save className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            )}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

function GroupRow({
  group,
  allGroups,
  disabled = false,
  onNameChange,
  onColorChange,
  onToggleIgnore,
  onDelete,
}) {
  const color = getGroupColor(group.colorId);
  const ignored = groupHasFlag(group, THREAD_GROUP_FLAG_IGNORE);
  const usedColorIds = useMemo(
    () => new Set(allGroups.filter((g) => g.id !== group.id).map((g) => g.colorId)),
    [allGroups, group.id],
  );

  return (
    <li className="flex items-center gap-2 rounded-md border border-border bg-card px-2 py-1.5 shadow-sm">
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label={`Color: ${color.label}`}
            className={cn(
              "h-6 w-6 shrink-0 rounded-full border border-border/60 shadow-inner transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              disabled ? "cursor-not-allowed opacity-60" : "",
              color.swatchClassName,
            )}
          />
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-2">
          <div className="grid grid-cols-5 gap-1.5">
            {GROUP_COLOR_PALETTE.map((entry) => {
              const isUsed = usedColorIds.has(entry.id);
              const isActive = group.colorId === entry.id;
              return (
                <button
                  key={entry.id}
                  type="button"
                  aria-label={entry.label}
                  title={isUsed && !isActive ? `${entry.label} (used)` : entry.label}
                  onClick={() => onColorChange(entry.id)}
                  className={cn(
                    "relative h-7 w-7 rounded-full border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    entry.swatchClassName,
                    isActive
                      ? "border-foreground ring-2 ring-ring"
                      : "border-border/60 hover:scale-110",
                    isUsed && !isActive ? "opacity-50" : "",
                  )}
                >
                  {isActive ? (
                    <Check className="absolute inset-0 m-auto h-4 w-4 text-white drop-shadow" strokeWidth={3} />
                  ) : null}
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>

      <Input
        value={group.name}
        onChange={(event) => onNameChange(event.target.value)}
        maxLength={40}
        disabled={disabled}
        className="h-8 flex-1 text-sm"
        placeholder="Group name"
      />

      <button
        type="button"
        onClick={onToggleIgnore}
        disabled={disabled}
        aria-pressed={ignored}
        aria-label={`Bot ignore for ${group.name}: ${ignored ? "on" : "off"}`}
        title="Bot ignores threads in this group — no replies, no locks, no tasks"
        className={cn(
          "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          ignored
            ? "border-amber-300 bg-amber-100 text-amber-700 dark:border-amber-500/40 dark:bg-amber-950/50 dark:text-amber-300"
            : "border-transparent text-muted-foreground",
          disabled ? "cursor-not-allowed opacity-50" : ignored ? "" : "hover:bg-accent",
        )}
      >
        <BotOff className="h-3.5 w-3.5" aria-hidden />
      </button>

      <button
        type="button"
        onClick={onDelete}
        disabled={disabled}
        aria-label={`Delete group ${group.name}`}
        className={cn(
          "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-transparent text-muted-foreground transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          disabled
            ? "cursor-not-allowed opacity-50"
            : "hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive",
        )}
      >
        <Trash2 className="h-3.5 w-3.5" aria-hidden />
      </button>
    </li>
  );
}
